import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, RotateCcw, Wand2, X } from 'lucide-react';
import { buttonVariants } from './ui/Button';
import { LocalizedAnchor } from './LocalizedLink';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import {
  planCtaHref,
  recommendPlan,
  type ComplianceAnswer,
  type NeedKey,
  type RetentionAnswer,
  type SystemsAnswer,
  type TeamAnswer,
  type WizardAnswers,
} from '@/lib/plan-advisor';

// Question copy lives in i18n under planWizard.*; the recommendation logic
// (and the reasoning behind the volume estimate) lives in lib/plan-advisor.ts.

type Draft = Partial<WizardAnswers>;

const SYSTEM_OPTIONS: SystemsAnswer[] = ['upTo5', 'upTo25', 'upTo100', 'over100'];
const TEAM_OPTIONS: TeamAnswer[] = ['upTo2', 'upTo10', 'over10'];
const NEED_OPTIONS: NeedKey[] = ['alerts', 'security', 'serviceHealth'];
const COMPLIANCE_OPTIONS: ComplianceAnswer[] = ['yes', 'no', 'unsure'];
const RETENTION_OPTIONS: RetentionAnswer[] = ['week', 'month', 'twoMonths', 'quarter', 'year'];

const STEP_COUNT = 5;

/* ---- Option controls ---- */

function OptionCard({
  selected,
  onClick,
  title,
  hint,
  multi,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  hint?: string;
  multi?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      className={cn(
        'group flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-600/20',
        selected
          ? 'border-primary-500 bg-primary-50 dark:border-primary-500/60 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-primary-500/40 dark:hover:bg-gray-800/40',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors',
          multi ? 'rounded-md' : 'rounded-full',
          selected
            ? 'border-primary-500 bg-primary-500 text-white'
            : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800',
        )}
        aria-hidden="true"
      >
        {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      <span>
        <span className="block text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
        {hint && (
          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">{hint}</span>
        )}
      </span>
    </button>
  );
}

function Step({
  title,
  hint,
  multi,
  children,
}: {
  title: string;
  hint?: string;
  /** Multi-select steps hold checkboxes, single-select steps hold radios. */
  multi?: boolean;
  children: React.ReactNode;
}) {
  const headingId = useId();

  return (
    <div>
      <h3 id={headingId} className="text-xl font-bold text-gray-900 dark:text-white text-balance">
        {title}
      </h3>
      {hint && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{hint}</p>}
      <div className="mt-6 space-y-3" role={multi ? 'group' : 'radiogroup'} aria-labelledby={headingId}>
        {children}
      </div>
    </div>
  );
}

/* ---- Result ---- */

function Result({ answers, onRestart }: { answers: WizardAnswers; onRestart: () => void }) {
  const t = useT();
  const advice = recommendPlan(answers);
  const isEnterprise = advice.plan === 'enterprise';

  return (
    <div>
      <p className="text-sm font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
        {t('planWizard.result.eyebrow')}
      </p>

      <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50 p-6 dark:border-primary-500/30 dark:bg-primary-900/15">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t(`pricing.${advice.plan}.name`)}
          </h3>
          <span className="text-lg font-semibold text-primary-700 dark:text-primary-300">
            {t(`pricing.${advice.plan}.price`)}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t(`pricing.${advice.plan}.subtext`)}
        </p>

        <ul className="mt-5 space-y-2.5" role="list">
          {advice.reasons.map((reason) => (
            <li key={reason} className="flex items-start gap-2.5">
              <Check
                className="mt-0.5 h-4 w-4 shrink-0 text-success-500 dark:text-success-400"
                aria-hidden="true"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{t(reason)}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        {t('planWizard.result.estimate').replace('{gb}', String(advice.estimatedGbPerDay))}
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        {isEnterprise ? (
          <LocalizedAnchor
            href="/contact"
            className={cn(buttonVariants({ variant: 'gradient', size: 'lg' }), 'flex-1 justify-center')}
          >
            {t('planWizard.result.ctaContact')}
            <ArrowRight className="ml-1 h-4 w-4" />
          </LocalizedAnchor>
        ) : (
          <a
            href={planCtaHref(advice.plan)}
            className={cn(buttonVariants({ variant: 'gradient', size: 'lg' }), 'flex-1 justify-center')}
          >
            {t('planWizard.result.ctaStart').replace('{plan}', t(`pricing.${advice.plan}.name`))}
            <ArrowRight className="ml-1 h-4 w-4" />
          </a>
        )}
        <button
          type="button"
          onClick={onRestart}
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'justify-center')}
        >
          <RotateCcw className="mr-1 h-4 w-4" />
          {t('planWizard.result.restart')}
        </button>
      </div>

      <p className="mt-5 text-xs leading-relaxed text-gray-500 dark:text-gray-500">
        {t('planWizard.result.disclaimer')}{' '}
        <LocalizedAnchor
          href="/pricing/compare"
          className="font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400"
        >
          {t('pricing.compareCta')}
        </LocalizedAnchor>
      </p>
    </div>
  );
}

/* ---- Modal shell ---- */

function WizardModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>({ needs: [] });

  const answers: WizardAnswers | null =
    draft.systems && draft.team && draft.compliance && draft.retention
      ? {
          systems: draft.systems,
          team: draft.team,
          needs: draft.needs ?? [],
          compliance: draft.compliance,
          retention: draft.retention,
        }
      : null;
  const done = step >= STEP_COUNT && answers !== null;

  useEffect(() => {
    if (!done || !answers) return;
    // The answers travel along, not just the outcome: what visitors say they
    // need (and whether they fall under the cybersecurity law) is the point of
    // asking. All of it is bucketed, so nothing identifying leaves the page.
    trackEvent('plan_wizard_completed', {
      plan: recommendPlan(answers).plan,
      systems: answers.systems,
      team: answers.team,
      compliance: answers.compliance,
      retention: answers.retention,
      needs: answers.needs.length > 0 ? [...answers.needs].sort().join('+') : 'none',
    });
    // The recommendation is a pure function of the answers, so reporting it
    // once per completed run is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  // Escape closes, and the page behind the modal must not scroll away.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep Tab inside the dialog; without this the focus ring walks off into
      // the pricing cards behind the overlay, which are visually unreachable.
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const pick = <K extends keyof WizardAnswers>(key: K, value: WizardAnswers[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setStep((current) => current + 1);
  };

  const toggleNeed = (need: NeedKey) => {
    setDraft((current) => {
      const needs = current.needs ?? [];
      return {
        ...current,
        needs: needs.includes(need) ? needs.filter((n) => n !== need) : [...needs, need],
      };
    });
  };

  const restart = () => {
    setDraft({ needs: [] });
    setStep(0);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl focus:outline-none dark:bg-gray-900 sm:rounded-2xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">
              {t('planWizard.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {done
                ? t('planWizard.doneSubtitle')
                : t('planWizard.progress')
                    .replace('{step}', String(step + 1))
                    .replace('{total}', String(STEP_COUNT))}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('planWizard.close')}
            className="-mr-2 -mt-2 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-600/20 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-600 to-primary-500 transition-all duration-300"
            style={{ width: `${(Math.min(step + 1, STEP_COUNT) / STEP_COUNT) * 100}%` }}
          />
        </div>

        <div className="mt-7">
          {done && answers ? (
            <Result answers={answers} onRestart={restart} />
          ) : (
            <>
              {step === 0 && (
                <Step title={t('planWizard.q.systems.title')} hint={t('planWizard.q.systems.hint')}>
                  {SYSTEM_OPTIONS.map((option) => (
                    <OptionCard
                      key={option}
                      selected={draft.systems === option}
                      onClick={() => pick('systems', option)}
                      title={t(`planWizard.q.systems.${option}`)}
                    />
                  ))}
                </Step>
              )}

              {step === 1 && (
                <Step title={t('planWizard.q.team.title')} hint={t('planWizard.q.team.hint')}>
                  {TEAM_OPTIONS.map((option) => (
                    <OptionCard
                      key={option}
                      selected={draft.team === option}
                      onClick={() => pick('team', option)}
                      title={t(`planWizard.q.team.${option}`)}
                    />
                  ))}
                </Step>
              )}

              {step === 2 && (
                <Step title={t('planWizard.q.needs.title')} hint={t('planWizard.q.needs.hint')} multi>
                  {NEED_OPTIONS.map((option) => (
                    <OptionCard
                      key={option}
                      multi
                      selected={(draft.needs ?? []).includes(option)}
                      onClick={() => toggleNeed(option)}
                      title={t(`planWizard.q.needs.${option}`)}
                      hint={t(`planWizard.q.needs.${option}.hint`)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className={cn(buttonVariants({ variant: 'gradient', size: 'lg' }), 'w-full justify-center')}
                  >
                    {(draft.needs ?? []).length === 0
                      ? t('planWizard.q.needs.none')
                      : t('planWizard.next')}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </button>
                </Step>
              )}

              {step === 3 && (
                <Step
                  title={t('planWizard.q.compliance.title')}
                  hint={t('planWizard.q.compliance.hint')}
                >
                  {COMPLIANCE_OPTIONS.map((option) => (
                    <OptionCard
                      key={option}
                      selected={draft.compliance === option}
                      onClick={() => pick('compliance', option)}
                      title={t(`planWizard.q.compliance.${option}`)}
                    />
                  ))}
                </Step>
              )}

              {step === 4 && (
                <Step
                  title={t('planWizard.q.retention.title')}
                  hint={t('planWizard.q.retention.hint')}
                >
                  {RETENTION_OPTIONS.map((option) => (
                    <OptionCard
                      key={option}
                      selected={draft.retention === option}
                      onClick={() => pick('retention', option)}
                      title={t(`planWizard.q.retention.${option}`)}
                    />
                  ))}
                </Step>
              )}
            </>
          )}
        </div>

        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep((current) => current - 1)}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('planWizard.back')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---- Trigger ---- */

export function PlanWizardButton() {
  const t = useT();
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const openWizard = () => {
    setOpen(true);
    trackEvent('plan_wizard_opened');
  };

  return (
    <>
      <button
        type="button"
        onClick={openWizard}
        className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'gap-2')}
      >
        <Wand2 className="h-4 w-4 text-primary-500" />
        {t('planWizard.trigger')}
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(<WizardModal onClose={close} />, document.body)
        : null}
    </>
  );
}
